import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useLocation } from 'react-router-dom';
import { UserOutlined } from '@ant-design/icons';
import AvatarField from '@components/common/form/AvatarField';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useListBase from '@hooks/useListBase';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { getFullImageUrl, orderNumber } from '@utils';

const message = defineMessages({
    objectName: 'Printer mapping',
});

function PrinterMappingListPage() {
    const translate = useTranslate();

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    
    const tab = searchParams.get('tab');
    const printerId = searchParams.get('printerId');
    const deviceId = searchParams.get('deviceId');
    const restaurantId = searchParams.get('restaurantId');
    const restaurantName = searchParams.get('restaurantName');
    const collapseId = searchParams.get('collapseId');

    const { data, mixinFuncs, loading, pagination, changePagination, pagePath } =
        useListBase({
            apiConfig: apiConfig.printerMapping,
            options: {
                pageSize: DEFAULT_TABLE_ITEM_SIZE,
                objectName: translate.formatMessage(message.objectName),
            },
            params: {
                printerId: printerId,
            },
            override: (funcs) => {
                funcs.mappingData = (response) => {
                    if (response.result === true) {
                        return {
                            data: response.data.content,
                            total: response.data.totalElements,
                        };
                    }
                };
                funcs.getList = () => {
                    mixinFuncs.handleFetchList({
                        printerId: printerId,
                    });
                };
                funcs.getCreateLink = () => {
                    return `${pagePath}/create?tab=${tab}&restaurantId=${restaurantId}&restaurantName=${restaurantName}&printerId=${printerId}&deviceId=${deviceId}`;
                };
                funcs.getItemDetailLink = (dataRow) => {
                    return `${pagePath}/${dataRow.id}?tab=${tab}&restaurantId=${restaurantId}&restaurantName=${restaurantName}&printerId=${printerId}&deviceId=${deviceId}`;
                };
            },
        });

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            key: 'id',
            render: (text, record, index) => {
                return orderNumber(pagination, index);
            },
            width: 50,
        },
        {
            title: <FormattedMessage defaultMessage="#" />,
            dataIndex: 'image',
            key: 'image',
            align: 'center',
            width: 70,
            render: (image) => {
                return (
                    <div style={{ backgroundColor: '#171717' }}>
                        <AvatarField size="large" icon={<UserOutlined />} src={getFullImageUrl(image)} disablePreview={true} />
                    </div>
                );
            },
        },
        {
            title: <FormattedMessage defaultMessage="Process" />,
            dataIndex: ['printerGroup', 'name'],
            width: 300,
        },
        {
            title: translate.formatMessage(commonMessage.printerNumber),
            dataIndex: 'printerNumber',
            render: (data) => {
                return (
                    <>
                        {translate.formatMessage(commonMessage.printerNumber)} {data}
                    </>
                );
            },
        },
        mixinFuncs.renderActionColumn(
            {
                edit: mixinFuncs.hasPermission([apiConfig.printerMapping.update.baseURL]),
                delete: mixinFuncs.hasPermission([apiConfig.printerMapping.delete.baseURL]),
            },
            { width: '120px' },
        ),
    ].filter(Boolean);

    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.restaurant),
                    path: generatePath(routes.restaurantListPage.path),
                },
                {
                    breadcrumbName: restaurantName || translate.formatMessage(commonMessage.devices),
                    path: `${generatePath(routes.resDeviceTabsRestaurantListPage.path)}?tab=${tab}&restaurantId=${restaurantId}&restaurantName=${restaurantName}&collapseId=${collapseId}`,
                },
                { breadcrumbName: translate.formatMessage(message.objectName) },
            ]}
        >
            <ListPage
                actionBar={mixinFuncs.renderActionBar()}
                baseTable={
                    <BaseTable
                        onChange={changePagination}
                        pagination={pagination}
                        loading={loading}
                        dataSource={data}
                        columns={columns}
                    />
                }
            />
        </PageWrapper>
    );
}
export default PrinterMappingListPage;
